import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { ReplaceDocumentFileDto } from '@dealstack/shared/lib/generated';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';

const replaceDocumentFileRequest = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	body,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	body: ReplaceDocumentFileDto;
}) => {
	const endpoint = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_REPLACE_FILE_POST,
		{
			dealId,
			programmeId,
			documentWorkflowInstanceId,
		}
	);
	return await makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const useReplaceDocumentFile = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onSuccess,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	onSuccess?: () => void;
}) => {
	const queryClient = useQueryClient();

	const handleSuccess = async () => {
		showToastV2({
			title: 'File replaced',
			state: 'success',
		});

		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_GET,
						{ dealId, programmeId, documentWorkflowInstanceId }
					),
				],
			}),
			queryClient.invalidateQueries({
				queryKey: getDocumentWorkflowEventsQueryKey({
					programmeId,
					documentWorkflowInstanceId,
				}),
			}),
			queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
						{
							dealId,
							programmeId,
						}
					),
				],
			}),
		]);

		onSuccess?.();
	};

	const handleError = () => {
		showToastV2({
			state: 'error',
			title: 'Failed to replace file',
		});
	};

	return useMutation({
		mutationFn: (body: ReplaceDocumentFileDto) =>
			replaceDocumentFileRequest({
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				body,
			}),
		onSuccess: handleSuccess,
		onError: handleError,
	});
};
